import { useNavigate } from 'react-router-dom';
import { useCallback, useState, useEffect, useMemo } from 'react';
import TwitchAppService from '../services/TwitchAppService';
import SpotifyAppService from '../services/SpotifyAppService';
import { useUserSession } from './useUserSession';
import { useAuth } from './useAuth';
import { ROUTES } from '../config/constants';
import NotificationService from '../services/NotificationService';

/**
 * Hook personnalisé pour gérer la logique de la page d'accueil
 * Gère les applications Twitch/Spotify, la navigation et l'authentification
 */
export function useHome() {
  const navigate = useNavigate();
  const { isUserAuthenticated, logout: logoutUserSession } = useUserSession();
  const { logoutTwitch, logoutSpotify, spotifyUser } = useAuth();

  // État pour la gestion des applications Twitch
  const [twitchApp, setTwitchApp] = useState(null);
  const [isLoadingTwitchApp, setIsLoadingTwitchApp] = useState(false);
  const [showTwitchAppSetup, setShowTwitchAppSetup] = useState(false);
  const [editingTwitchApp, setEditingTwitchApp] = useState(null); // App en cours d'édition

  // État pour la gestion des applications Spotify
  const [spotifyApp, setSpotifyApp] = useState(null);
  const [isLoadingSpotifyApp, setIsLoadingSpotifyApp] = useState(false);
  const [showSpotifyAppSetup, setShowSpotifyAppSetup] = useState(false);
  const [editingSpotifyApp, setEditingSpotifyApp] = useState(null); // App en cours d'édition

  // État pour la modal de connexion
  const [showLoginModal, setShowLoginModal] = useState(false);
  /** Redirection après connexion (ex. /dashboard quand on vient de RequireUserAuth) */
  const [redirectAfterLogin, setRedirectAfterLogin] = useState('');

  // Fonction générique pour charger une application
  const loadApp = useCallback(async (service, setApp, setLoading) => {
    try {
      setLoading(true);
      const result = await service.getUserApp();
      setApp(result);
    } catch (error) {
      console.error(`Erreur lors du chargement de l'application:`, error);
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger l'application Twitch de l'utilisateur
  const loadTwitchApp = useCallback(() => {
    return loadApp(TwitchAppService, setTwitchApp, setIsLoadingTwitchApp);
  }, [loadApp]);

  // Charger l'application Spotify de l'utilisateur
  const loadSpotifyApp = useCallback(() => {
    return loadApp(SpotifyAppService, setSpotifyApp, setIsLoadingSpotifyApp);
  }, [loadApp]);

  // Ouvrir le modal de configuration d'app Twitch (création)
  const openTwitchAppSetup = useCallback(() => {
    if (!isUserAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setEditingTwitchApp(null); // Mode création
    setShowTwitchAppSetup(true);
  }, [isUserAuthenticated]);

  // Ouvrir le modal d'édition d'app Twitch
  const openTwitchAppEdit = useCallback(() => {
    if (!isUserAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (twitchApp) {
      setEditingTwitchApp(twitchApp); // Mode édition
      setShowTwitchAppSetup(true);
    }
  }, [isUserAuthenticated, twitchApp]);

  // Fermer le modal de configuration d'app Twitch
  const closeTwitchAppSetup = useCallback(() => {
    setShowTwitchAppSetup(false);
    setEditingTwitchApp(null);
  }, []);

  // Callback appelé quand une app Twitch est créée/modifiée avec succès
  const handleTwitchAppCreated = useCallback((app) => {
    setTwitchApp(app);
    setShowTwitchAppSetup(false);
    setEditingTwitchApp(null);
  }, []);

  // Ouvrir le modal de configuration d'app Spotify (création)
  const openSpotifyAppSetup = useCallback(() => {
    if (!isUserAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setEditingSpotifyApp(null); // Mode création
    setShowSpotifyAppSetup(true);
  }, [isUserAuthenticated]);

  // Ouvrir le modal d'édition d'app Spotify
  const openSpotifyAppEdit = useCallback(() => {
    if (!isUserAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (spotifyApp) {
      setEditingSpotifyApp(spotifyApp); // Mode édition
      setShowSpotifyAppSetup(true);
    }
  }, [isUserAuthenticated, spotifyApp]);

  // Fermer le modal de configuration d'app Spotify
  const closeSpotifyAppSetup = useCallback(() => {
    setShowSpotifyAppSetup(false);
    setEditingSpotifyApp(null);
  }, []);

  // Callback appelé quand une app Spotify est créée/modifiée avec succès
  const handleSpotifyAppCreated = useCallback((app) => {
    setSpotifyApp(app);
    setShowSpotifyAppSetup(false);
    setEditingSpotifyApp(null);
  }, []);

  // Gestion de la modal de connexion
  const openLoginModal = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  // Charger les apps au montage du composant (uniquement si authentifié)
  useEffect(() => {
    if (isUserAuthenticated) {
      loadTwitchApp();
      loadSpotifyApp();
    }
  }, [isUserAuthenticated, loadTwitchApp, loadSpotifyApp]);

  // Nettoyer l'app Spotify si l'utilisateur Spotify se déconnecte
  // (surtout pour les apps système qui nécessitent un utilisateur connecté)
  useEffect(() => {
    if (!spotifyUser && spotifyApp?.isSystemApp) {
      setSpotifyApp(null);
    }
  }, [spotifyUser, spotifyApp]);

  // Vérifier si on doit ouvrir automatiquement la modal de connexion
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginParam = urlParams.get('login');
    const nextParam = urlParams.get('next');

    if (loginParam === '1') {
      setShowLoginModal(true);
      if (nextParam) {
        setRedirectAfterLogin(nextParam);
      }
      // Nettoyer l'URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('login');
      newUrl.searchParams.delete('next');
      window.history.replaceState(
        {},
        document.title,
        newUrl.pathname + newUrl.search
      );
    }
  }, []);

  // Gérer les erreurs d'authentification renvoyées par l'URL (ex. Twitch credentials non configurés)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');

    if (errorParam === 'twitch_signup_disabled') {
      NotificationService.error(
        "Création de compte via Twitch désactivée. Connectez-vous d'abord avec email et mot de passe."
      );
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState(
        {},
        document.title,
        newUrl.pathname + newUrl.search
      );
    } else if (errorParam === 'validation_error') {
      const message =
        process.env.NODE_ENV === 'development'
          ? 'Connexion Twitch impossible : les identifiants Twitch ne sont pas configurés sur le serveur. Configurez TWITCH_CLIENT_ID et TWITCH_CLIENT_SECRET dans backend/.env (voir readme).'
          : 'Connexion Twitch impossible. Veuillez réessayer plus tard ou contacter le support.';
      NotificationService.error(message);
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState(
        {},
        document.title,
        newUrl.pathname + newUrl.search
      );
    } else if (errorParam === 'oauth_error' || errorParam === 'auth_error') {
      NotificationService.error(
        'Erreur lors de la connexion Twitch. Veuillez réessayer.'
      );
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState(
        {},
        document.title,
        newUrl.pathname + newUrl.search
      );
    }
  }, []);

  const navigateProfile = useCallback(() => {
    navigate(ROUTES.PROFILE);
  }, [navigate]);

  const goToDashboard = useCallback(() => {
    navigate(ROUTES.DASHBOARD);
  }, [navigate]);

  // Fonction de déconnexion Spotify qui met à jour l'état de l'application
  const handleSpotifyLogout = useCallback(async () => {
    try {
      await logoutSpotify();
      // Réinitialiser l'état de l'application Spotify localement
      setSpotifyApp(null);
      // Ne pas recharger l'app après déconnexion - elle ne devrait pas être affichée
      // si l'utilisateur Spotify n'est pas connecté
    } catch (error) {
      console.error('Erreur lors de la déconnexion Spotify:', error);
      // En cas d'erreur, réinitialiser quand même l'état local
      setSpotifyApp(null);
    }
  }, [logoutSpotify]);

  // Fonction de déconnexion générale
  const logout = useCallback(async () => {
    try {
      // Déconnecter des plateformes connectées en parallèle
      await Promise.allSettled([logoutTwitch(), logoutSpotify()]);
      // Déconnecter la session utilisateur
      await logoutUserSession();
      // Réinitialiser les états locaux
      setTwitchApp(null);
      setSpotifyApp(null);
      // Rafraîchir la page pour s'assurer que tous les états sont remis à zéro
      window.location.reload();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Forcer le rechargement même en cas d'erreur
      window.location.reload();
    }
  }, [logoutTwitch, logoutSpotify, logoutUserSession]);

  // Mémoriser l'objet de retour pour éviter les re-renders inutiles
  return useMemo(
    () => ({
      navigateProfile,
      goToDashboard,
      logout,
      logoutSpotify: handleSpotifyLogout,
      // Gestion des applications Twitch
      twitchApp,
      isLoadingTwitchApp,
      showTwitchAppSetup,
      openTwitchAppSetup,
      openTwitchAppEdit,
      closeTwitchAppSetup,
      handleTwitchAppCreated,
      loadTwitchApp,
      editingTwitchApp,
      // Gestion des applications Spotify
      spotifyApp,
      isLoadingSpotifyApp,
      showSpotifyAppSetup,
      openSpotifyAppSetup,
      openSpotifyAppEdit,
      closeSpotifyAppSetup,
      handleSpotifyAppCreated,
      loadSpotifyApp,
      editingSpotifyApp,
      // Gestion de la modal de connexion
      showLoginModal,
      openLoginModal,
      closeLoginModal,
      redirectAfterLogin,
    }),
    [
      navigateProfile,
      goToDashboard,
      logout,
      handleSpotifyLogout,
      twitchApp,
      isLoadingTwitchApp,
      showTwitchAppSetup,
      openTwitchAppSetup,
      openTwitchAppEdit,
      closeTwitchAppSetup,
      handleTwitchAppCreated,
      loadTwitchApp,
      editingTwitchApp,
      spotifyApp,
      isLoadingSpotifyApp,
      showSpotifyAppSetup,
      openSpotifyAppSetup,
      openSpotifyAppEdit,
      closeSpotifyAppSetup,
      handleSpotifyAppCreated,
      loadSpotifyApp,
      editingSpotifyApp,
      showLoginModal,
      openLoginModal,
      closeLoginModal,
      redirectAfterLogin,
    ]
  );
}
